import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  Avatar,
  DataTable,
  Empty,
  Muted,
  Section,
  StatusBadge,
  Tabs,
} from '@/components/ui'
import { deleteColaborador, fetchColaborador, fetchColaboradorTimeline } from './api'
import type { ColaboradorDetalhe, DependenteItem, HistoricoSalarialItem } from './types'
import { formatCpf, formatDate, formatMoney } from './utils'

type TabId =
  | 'pessoal'
  | 'contratual'
  | 'documentos'
  | 'bancario'
  | 'dependentes'
  | 'cargo'
  | 'historico'
  | 'timeline'

const TABS: { id: TabId; label: string }[] = [
  { id: 'pessoal', label: 'Dados pessoais' },
  { id: 'contratual', label: 'Contratual' },
  { id: 'documentos', label: 'Docs. trabalhistas' },
  { id: 'bancario', label: 'Bancário' },
  { id: 'dependentes', label: 'Dependentes' },
  { id: 'cargo', label: 'Cargo' },
  { id: 'historico', label: 'Hist. salarial' },
  { id: 'timeline', label: 'Timeline' },
]

function dash(v?: string | number | null) {
  if (v == null || v === '') return '—'
  return String(v)
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="hi-field">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

function Fields({ items }: { items: Array<[string, string]> }) {
  return (
    <div className="hi-fields">
      {items.map(([label, value]) => (
        <Field key={label} label={label} value={value} />
      ))}
    </div>
  )
}

function enderecoCompleto(c: ColaboradorDetalhe) {
  const parts = [
    c.logradouro,
    c.numero,
    c.complemento,
    c.bairro,
    c.cidade && c.estado ? `${c.cidade}/${c.estado}` : c.cidade || c.estado,
    c.cep,
  ].filter(Boolean)
  return parts.length ? parts.join(', ') : '—'
}

export function ColaboradorDetailPage() {
  const { colaboradorId = '' } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { canEdit } = useAuth()
  const [tab, setTab] = useState<TabId>('pessoal')
  const [msg, setMsg] = useState('')
  const rhWrite = canEdit('colaboradores')

  const colab = useQuery({
    queryKey: ['rh', 'colaborador', colaboradorId],
    queryFn: () => fetchColaborador(colaboradorId),
    enabled: Boolean(colaboradorId),
  })

  const timeline = useQuery({
    queryKey: ['rh', 'timeline', colaboradorId],
    queryFn: () => fetchColaboradorTimeline(colaboradorId),
    enabled: Boolean(colaboradorId) && tab === 'timeline',
  })

  const remover = useMutation({
    mutationFn: () => deleteColaborador(colaboradorId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['rh'] })
      navigate('/app/rh')
    },
    onError: (err) => {
      setMsg(err instanceof ApiError ? err.message : 'Falha ao excluir.')
    },
  })

  if (colab.isLoading) return <Muted>Carregando colaborador…</Muted>
  if (colab.isError || !colab.data) {
    return (
      <div className="hi-page">
        <Alert tone="danger">
          {colab.error instanceof ApiError ? colab.error.message : 'Não encontrado.'}
        </Alert>
        <div className="hi-actions">
          <Link className="btn btn-secondary" to="/app/rh">
            Voltar
          </Link>
        </div>
      </div>
    )
  }

  const c = colab.data
  const foto = c.foto_url || c.foto
  const cargo = c.cargo_descritivo
  const deps = (c.dependentes || []) as DependenteItem[]
  const hist = (c.historico_salarial || []) as HistoricoSalarialItem[]
  const legacyHash = `#/colaboradores`

  return (
    <section className="hi-page">
      <Link className="hi-crumb" to="/app/rh">
        ‹ Colaboradores
      </Link>

      <div className="card hi-profile-head">
        <Avatar src={foto} nome={c.nome} iniciais={c.iniciais} size="lg" />
        <div>
          <h4>{c.nome}</h4>
          <p>
            {c.cargo_nome || 'Sem cargo'}
            {c.departamento_nome ? ` · ${c.departamento_nome}` : ''}
          </p>
          <p style={{ marginTop: 4 }}>
            {c.empresa_nome || c.empresa_fantasia ? (
              <>
                {c.empresa_fantasia || c.empresa_nome}
                {c.codigo_empresa_dominio ? ` · cód. ${c.codigo_empresa_dominio}` : ''}
                {' · '}
              </>
            ) : c.codigo_empresa_dominio ? (
              <>Empresa Domínio {c.codigo_empresa_dominio} (sem vínculo) · </>
            ) : null}
            Mat. {dash(c.matricula)} · Admissão {formatDate(c.data_admissao)} ·{' '}
            {dash(c.tempo_empresa)} de empresa
          </p>
          <p style={{ marginTop: 6 }}>
            <StatusBadge tone={c.status && c.status !== 'ativo' ? 'inativo' : 'ativo'}>
              {c.status_display || c.status || '—'}
            </StatusBadge>
            <span style={{ marginLeft: 10 }}>{formatMoney(c.salario_base)}/mês</span>
          </p>
        </div>
        <div className="hi-actions">
          {rhWrite ? (
            <>
              <Link className="btn btn-primary" to={`/app/rh/${colaboradorId}/editar`}>
                Editar
              </Link>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={remover.isPending}
                onClick={() => {
                  if (
                    confirm(
                      `Excluir o colaborador “${c.nome}”? Esta ação é um soft-delete e pode ser revertida pelo RH no admin.`,
                    )
                  ) {
                    remover.mutate()
                  }
                }}
              >
                {remover.isPending ? 'Excluindo…' : 'Excluir'}
              </button>
            </>
          ) : null}
          <a className="btn btn-secondary" href={legacyAppUrl(legacyHash)}>
            Abrir no legado
          </a>
        </div>
      </div>

      {msg ? <Alert tone="danger">{msg}</Alert> : null}

      <Tabs items={TABS} value={tab} onChange={setTab} label="Abas do colaborador" />

      {tab === 'pessoal' ? (
        <>
          <Section title="Dados pessoais">
            <Fields
              items={[
                ['CPF', formatCpf(c.cpf)],
                ['RG', dash(c.rg)],
                ['Nome social', dash(c.nome_social)],
                ['Nascimento', formatDate(c.data_nascimento)],
                ['Sexo', dash(c.sexo_display || c.sexo)],
                ['Estado civil', dash(c.estado_civil_display || c.estado_civil)],
                ['Nacionalidade', dash(c.nacionalidade)],
                ['Naturalidade', dash(c.naturalidade)],
                ['UF nascimento', dash(c.uf_nascimento)],
                ['Grau instrução', dash(c.grau_instrucao)],
                ['Raça/cor', dash(c.raca_cor)],
                ['Naturalizado', c.naturalizado ? 'Sim' : 'Não'],
                ['Aposentado', c.aposentado ? 'Sim' : 'Não'],
                ['PCD / cota', c.pcd ? 'Sim' : 'Não'],
              ]}
            />
          </Section>
          <Section title="Filiação">
            <Fields
              items={[
                ['Nome do pai', dash(c.nome_pai)],
                ['Nome da mãe', dash(c.nome_mae)],
                ['Cônjuge', dash(c.nome_conjuge)],
              ]}
            />
          </Section>
          <Section title="Contato">
            <Fields
              items={[
                [
                  'E-mail profissional',
                  dash(c.email_profissional || c.email_corporativo),
                ],
                ['E-mail corporativo', dash(c.email_corporativo)],
                ['E-mail pessoal', dash(c.email)],
                ['E-mail alternativo', dash(c.email_alternativo)],
                ['E-mail portal', dash(c.email_portal)],
                ['Telefone', dash(c.telefone)],
                ['Celular', dash(c.celular)],
              ]}
            />
          </Section>
          <Section title="Endereço">
            <Fields
              items={[
                ['Endereço completo', enderecoCompleto(c)],
                ['Logradouro', dash(c.logradouro)],
                ['Número', dash(c.numero)],
                ['Complemento', dash(c.complemento)],
                ['Bairro', dash(c.bairro)],
                ['Cidade', dash(c.cidade)],
                ['UF', dash(c.estado)],
                ['CEP', dash(c.cep)],
              ]}
            />
          </Section>
        </>
      ) : null}

      {tab === 'contratual' ? (
        <>
          {c.codigo_empresa_dominio && !c.empresa_id && !c.empresa_nome ? (
            <Alert tone="info">
              Código de empresa Domínio <strong>{c.codigo_empresa_dominio}</strong> precisa ser
              cadastrado. Importe <Link to="/app/empresas/importar">empresas.wk1</Link> para
              vincular.
            </Alert>
          ) : null}
          {c.codigo_cargo_dominio && !c.cargo_id && !c.cargo_nome ? (
            <Alert tone="info">
              Código de cargo Domínio{' '}
              <strong>
                {[c.codigo_empresa_dominio, c.codigo_cargo_dominio].filter(Boolean).join(':') ||
                  c.codigo_cargo_dominio}
              </strong>{' '}
              precisa ser cadastrado. Importe <Link to="/app/cargos/importar">cargos.wk1</Link> ou
              cadastre o cargo com esse código para vincular.
            </Alert>
          ) : null}
          <Section title="Dados contratuais">
            <Fields
              items={[
                ['Matrícula', dash(c.matricula)],
                ['Regime', dash(c.regime_display || (c.regime || '').toUpperCase())],
                ['Status', dash(c.status_display || c.status)],
                ['Admissão', formatDate(c.data_admissao)],
                ['Demissão', formatDate(c.data_demissao)],
                ['Data afastamento Domínio', formatDate(c.data_afastamento)],
                ['Cód. afastamento', dash(c.codigo_afastamento)],
                ['Tempo de empresa', dash(c.tempo_empresa)],
                [
                  'Empresa',
                  c.empresa_nome
                    ? c.empresa_fantasia && c.empresa_fantasia !== c.empresa_nome
                      ? `${c.empresa_nome} (${c.empresa_fantasia})`
                      : c.empresa_nome
                    : c.codigo_empresa_dominio
                      ? `Cód. ${c.codigo_empresa_dominio} (precisa cadastrar)`
                      : '—',
                ],
                [
                  'Empresa Domínio',
                  c.codigo_empresa_dominio
                    ? c.empresa_id || c.empresa_nome
                      ? c.codigo_empresa_dominio
                      : `${c.codigo_empresa_dominio} (precisa cadastrar)`
                    : '—',
                ],
                [
                  'CNPJ empresa',
                  c.empresa_cnpj
                    ? c.empresa_cnpj.replace(
                        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
                        '$1.$2.$3/$4-$5',
                      )
                    : '—',
                ],
                ['Cargo', dash(c.cargo_nome)],
                [
                  'Cargo Domínio',
                  (() => {
                    if (!c.codigo_cargo_dominio) return '—'
                    const chave =
                      [c.codigo_empresa_dominio, c.codigo_cargo_dominio]
                        .filter(Boolean)
                        .join(':') || c.codigo_cargo_dominio
                    return c.cargo_id || c.cargo_nome
                      ? chave
                      : `${chave} (precisa cadastrar)`
                  })(),
                ],
                ['Departamento', dash(c.departamento_nome)],
                ['Centro de custo', dash(c.departamento_centro_custo || c.centro_custo_codigo)],
                ['Cód. CCusto Domínio', dash(c.centro_custo_codigo)],
                ['Gestor', dash(c.gestor_nome)],
                ['Salário base', formatMoney(c.salario_base)],
                ['Forma pagamento', dash(c.forma_pagamento)],
                ['Vínculo Domínio', dash(c.vinculo_dominio)],
                ['Venc. férias', formatDate(c.vencimento_ferias)],
                ['Último exame médico', formatDate(c.ultimo_exame_medico)],
                ['Carga horária', c.carga_horaria != null ? `${c.carga_horaria}h` : '—'],
                ['RH ID', c.id_rhid != null ? String(c.id_rhid) : '—'],
                ['Bitrix ID', dash(c.id_bitrix)],
                ['Filial Domínio', dash(c.codigo_filial_dominio)],
                ['Categoria Domínio', dash(c.categoria_dominio)],
                ['Depto Domínio', dash(c.codigo_depto_dominio)],
                [
                  'Onboarding',
                  c.onboarding_completo
                    ? 'Completo'
                    : `${c.onboarding_pct ?? 0}%`,
                ],
              ]}
            />
          </Section>
          {c.observacoes ? (
            <Section title="Observações">
              <p>{c.observacoes}</p>
            </Section>
          ) : null}
        </>
      ) : null}

      {tab === 'documentos' ? (
        <Section title="Documentos trabalhistas">
          <Fields
            items={[
              ['PIS/PASEP', dash(c.pis_pasep)],
              ['RG órgão', dash(c.rg_orgao_emissor)],
              ['RG UF', dash(c.rg_uf)],
              ['RG emissão', formatDate(c.rg_data_emissao)],
              ['CTPS número', dash(c.ctps_numero)],
              ['CTPS série', dash(c.ctps_serie)],
              ['CTPS UF', dash(c.ctps_uf)],
              ['Título de eleitor', dash(c.titulo_eleitor)],
              ['CNH número', dash(c.cnh_numero)],
              ['CNH categoria', dash(c.cnh_categoria)],
              ['CNH validade', formatDate(c.cnh_validade)],
              ['Opção FGTS', formatDate(c.data_opcao_fgts)],
              ['Conta FGTS', dash(c.conta_fgts)],
            ]}
          />
          <p className="card-subtitle" style={{ marginTop: 12 }}>
            Anexos do GED:{' '}
            <Link to="/app/documentos">Documentos</Link> ·{' '}
            <a href={legacyAppUrl(legacyHash)}>legado</a>
          </p>
        </Section>
      ) : null}

      {tab === 'bancario' ? (
        <Section title="Dados bancários">
          <Fields
            items={[
              ['Banco', dash(c.banco)],
              ['Agência', dash(c.agencia)],
              ['Conta', dash(c.conta)],
              ['Tipo de conta', dash(c.tipo_conta)],
              ['PIX tipo', dash(c.pix_tipo)],
              ['PIX chave', dash(c.pix_chave)],
            ]}
          />
        </Section>
      ) : null}

      {tab === 'dependentes' ? (
        <Section title="Dependentes" flush>
          {deps.length === 0 ? <Empty>Nenhum dependente cadastrado.</Empty> : null}
          {deps.length > 0 ? (
            <DataTable>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Parentesco</th>
                  <th>CPF</th>
                  <th>Nascimento</th>
                  <th>IR</th>
                </tr>
              </thead>
              <tbody>
                {deps.map((d, i) => (
                  <tr key={d.id || i}>
                    <td>{d.nome || '—'}</td>
                    <td>{d.parentesco || '—'}</td>
                    <td>{formatCpf(d.cpf)}</td>
                    <td>{formatDate(d.data_nascimento)}</td>
                    <td>{d.ir ? 'Sim' : 'Não'}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : null}
        </Section>
      ) : null}

      {tab === 'cargo' ? (
        <Section title="Descritivo do cargo">
          {!cargo ? (
            <Empty>Sem cargo vinculado.</Empty>
          ) : (
            <>
              <Fields
                items={[
                  ['Cargo', dash(cargo.nome)],
                  ['Área', dash(cargo.area)],
                  ['CBO', dash(cargo.codigo_cbo)],
                  ['Nível', dash(cargo.nivel)],
                  ['Grade', cargo.nivel_grade != null ? String(cargo.nivel_grade) : '—'],
                  [
                    'Faixa salarial',
                    `${formatMoney(cargo.salario_min)} – ${formatMoney(cargo.salario_max)}`,
                  ],
                ]}
              />
              {cargo.objetivo ? (
                <>
                  <h4 className="card-title" style={{ marginTop: 16 }}>
                    Objetivo
                  </h4>
                  <p>{cargo.objetivo}</p>
                </>
              ) : null}
              {cargo.responsabilidades ? (
                <>
                  <h4 className="card-title" style={{ marginTop: 16 }}>
                    Responsabilidades
                  </h4>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{cargo.responsabilidades}</p>
                </>
              ) : null}
              {cargo.formacao ? (
                <>
                  <h4 className="card-title" style={{ marginTop: 16 }}>
                    Formação
                  </h4>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{cargo.formacao}</p>
                </>
              ) : null}
              {(cargo.tags_tecnicas || []).length > 0 ? (
                <>
                  <h4 className="card-title" style={{ marginTop: 16 }}>
                    Competências técnicas
                  </h4>
                  <p>{(cargo.tags_tecnicas || []).map((t) => t.nome).join(', ')}</p>
                </>
              ) : cargo.competencias_tecnicas ? (
                <>
                  <h4 className="card-title" style={{ marginTop: 16 }}>
                    Competências técnicas
                  </h4>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{cargo.competencias_tecnicas}</p>
                </>
              ) : null}
              {(cargo.tags_comportamentais || []).length > 0 ? (
                <>
                  <h4 className="card-title" style={{ marginTop: 16 }}>
                    Competências comportamentais
                  </h4>
                  <p>{(cargo.tags_comportamentais || []).map((t) => t.nome).join(', ')}</p>
                </>
              ) : cargo.competencias_comportamentais ? (
                <>
                  <h4 className="card-title" style={{ marginTop: 16 }}>
                    Competências comportamentais
                  </h4>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{cargo.competencias_comportamentais}</p>
                </>
              ) : null}
            </>
          )}
        </Section>
      ) : null}

      {tab === 'historico' ? (
        <Section title="Histórico salarial" flush>
          {hist.length === 0 ? <Empty>Sem reajustes registrados.</Empty> : null}
          {hist.length > 0 ? (
            <DataTable>
              <thead>
                <tr>
                  <th>Vigência</th>
                  <th>Anterior</th>
                  <th>Novo</th>
                  <th>%</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                {hist.map((h, i) => (
                  <tr key={h.id || i}>
                    <td>{formatDate(h.vigencia)}</td>
                    <td>{formatMoney(h.salario_anterior)}</td>
                    <td>{formatMoney(h.salario_novo)}</td>
                    <td>
                      {h.percentual != null && h.percentual !== ''
                        ? `${Number(h.percentual).toLocaleString('pt-BR')}%`
                        : '—'}
                    </td>
                    <td>{h.motivo || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : null}
        </Section>
      ) : null}

      {tab === 'timeline' ? (
        <Section title="Linha do tempo">
          <p className="card-subtitle" style={{ marginBottom: 12 }}>
            Admissão, reajustes, avaliações e todas as alterações feitas na ficha (CRUD).
          </p>
          {timeline.isLoading ? <Muted>Carregando…</Muted> : null}
          {timeline.isError ? (
            <Alert tone="danger">
              {timeline.error instanceof ApiError
                ? timeline.error.message
                : 'Timeline indisponível.'}
            </Alert>
          ) : null}
          {!timeline.isLoading && (timeline.data || []).length === 0 ? (
            <Empty>Sem eventos registrados.</Empty>
          ) : null}
          {(timeline.data || []).length > 0 ? (
            <ul className="hi-feed">
              {(timeline.data || []).map((ev, i) => (
                <li key={i}>
                  <strong>
                    {ev.icon ? `${String(ev.icon)} ` : ''}
                    {String(ev.titulo || ev.tipo || 'Evento')}
                  </strong>
                  <span>
                    {formatDate(String(ev.data || ''))}
                    {ev.autor ? ` · por ${String(ev.autor)}` : ''}
                  </span>
                  {ev.descricao ? (
                    <span style={{ display: 'block', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                      {String(ev.descricao)}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </Section>
      ) : null}
    </section>
  )
}

export default ColaboradorDetailPage
